import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Linkedin, Mail, Github, ArrowLeft, Code, Server, Database, Globe, Smartphone, GraduationCap } from 'lucide-react';
import denizImage from '../assets/team/deniz.png';

// ------------------
// Styled Components
// ------------------
const PageContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  min-height: 100vh;
  padding-top: 20px;
`;

const MemberContainer = styled.div`
  padding: 30px 50px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 20px 20px;
  }
`;

const Navigation = styled(motion.nav)`
  margin-bottom: 50px;
`;

const BackLink = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: rgba(30, 30, 30, 0.5);
  color: ${({ theme }) => theme.colors.text.secondary};
  box-shadow: ${({ theme }) => theme.shadows.small};
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  
  &:hover {
    color: white;
    background: ${({ theme }) => theme.colors.accentColor.primary};
    transform: translateX(-5px);
    box-shadow: ${({ theme }) => theme.shadows.accent};
  }
`;

const ProfileSection = styled(motion.section)`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 50px;
  margin-bottom: 70px;
  align-items: start;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ProfileImageContainer = styled(motion.div)`
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  background: #ffffff;
  width: fit-content;
  align-self: flex-start; /* Keep image at the top */
`;

const ProfileImage = styled.img`
  max-width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: block;
`;

const ProfileInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const ProfileName = styled(motion.h1)`
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colors.gradient.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ProfileTitle = styled(motion.h2)`
  font-size: 1.2rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 25px;
`;

const ProfileBio = styled(motion.p)`
  font-size: 1.05rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 30px;
`;

const SectionTitle = styled(motion.h3)`
  font-size: 1.4rem;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 20px;
  position: relative;
  padding-left: 15px;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    border-radius: ${({ theme }) => theme.borderRadius.small};
  }
`;

const ExpertiseSection = styled(motion.div)`
  background: rgba(30, 30, 30, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 30px;
  margin-bottom: 40px;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const ExpertiseContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ExpertiseText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1rem;
  line-height: 1.7;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
`;

const SkillItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 91, 58, 0.10);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const SkillIcon = styled.div`
  color: ${({ theme }) => theme.colors.accentColor.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EducationItem = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const EducationIcon = styled.div`
  color: ${({ theme }) => theme.colors.accentColor.primary};
  background: rgba(255, 91, 58, 0.10);
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const EducationContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const EducationDegree = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 5px;
`;

const EducationSchool = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ContactSection = styled(ExpertiseSection)`
  margin-bottom: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: rgba(255, 255, 255, 0.05);
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    background: ${({ theme }) => theme.colors.accentColor.primary};
    color: white;
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.accent};
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const skillVariant = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  }
};

// ------------------
// Main Component
// ------------------
const DenizErenGencay = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    // Slight delay to ensure animations trigger after navigation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <PageContainer>
      <MemberContainer>
        {/* Navigation */}
        <Navigation
          initial={{ opacity: 0, x: -20 }}
          animate={isVisible ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" style={{ textDecoration: 'none' }}>
            <BackLink
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeft size={24} />
            </BackLink>
          </Link>
        </Navigation>

        {/* Profile Section */}
        <ProfileSection
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
        >
          <ProfileImageContainer
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ProfileImage src={denizImage} alt="Deniz Eren Gencay" loading="eager" decoding="async" fetchpriority="high" />
          </ProfileImageContainer>

          <ProfileInfo>
            <ProfileName
              initial={{ opacity: 0, y: -20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Deniz Eren Gencay
            </ProfileName>
            
            <ProfileTitle
              initial={{ opacity: 0, y: -20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Senior Software Engineer &amp; Co-Founder
            </ProfileTitle>
            
            <ProfileBio
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Leveraging a background in Electrical and Electronics Engineering, 
              I focus on developing robust mobile and full-stack solutions. While 
              pursuing a Master's degree in Computer Engineering at Politecnico di Torino, 
              I also co-manage a growing consultancy to deliver modern, scalable tech platforms.
            </ProfileBio>

            {/* Professional Experience */}
            <ExpertiseSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.6 }}
            >
              <SectionTitle>Professional Achievements</SectionTitle>
              <ExpertiseContent>
                <ExpertiseText>
                  My career took shape at Vestel, where I advanced into a Senior Software 
                  Specialist role, driving critical mobile projects. Through hands-on 
                  collaboration and leadership, I've supported the successful launch 
                  of high-visibility digital products.
                </ExpertiseText>
                <SkillsList>
                  <SkillItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                    transition={{ delay: 0.7 }}
                  >
                    <SkillIcon><Code size={16} /></SkillIcon>
                    Senior Software Engineer
                  </SkillItem>
                  <SkillItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                    transition={{ delay: 0.8 }}
                  >
                    <SkillIcon><Smartphone size={16} /></SkillIcon>
                    Mobile Engineer
                  </SkillItem>
                  <SkillItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                    transition={{ delay: 0.9 }}
                  >
                    <SkillIcon><Code size={16} /></SkillIcon>
                    Full Stack Engineer
                  </SkillItem>
                </SkillsList>
              </ExpertiseContent>
            </ExpertiseSection>

            {/* Technical Expertise */}
            <ExpertiseSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.7 }}
            >
              <SectionTitle>Technical Mastery</SectionTitle>
              <SkillsList>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Code size={16} /></SkillIcon>
                  Full Stack Development
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Code size={16} /></SkillIcon>
                  React & Next.js
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Server size={16} /></SkillIcon>
                  Node.js
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Code size={16} /></SkillIcon>
                  Python
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Smartphone size={16} /></SkillIcon>
                  Mobile Development (iOS & Android)
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Smartphone size={16} /></SkillIcon>
                  Flutter & React Native
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Smartphone size={16} /></SkillIcon>
                  App Store & Play Store Submission
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Globe size={16} /></SkillIcon>
                  Generative AI & LLM Integration
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Server size={16} /></SkillIcon>
                  AWS & GCP
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Database size={16} /></SkillIcon>
                  Cloud Architecture
                </SkillItem>
              </SkillsList>
            </ExpertiseSection>

            {/* Education */}
            <ExpertiseSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.8 }}
            >
              <SectionTitle>Education &amp; Research</SectionTitle>
              <ExpertiseContent>
                <EducationItem>
                  <EducationIcon>
                    <GraduationCap size={20} />
                  </EducationIcon>
                  <EducationContent>
                    <EducationDegree>MSc in Computer Engineering</EducationDegree>
                    <EducationSchool>Politecnico di Torino</EducationSchool>
                  </EducationContent>
                </EducationItem>
                <EducationItem>
                  <EducationIcon>
                    <GraduationCap size={20} />
                  </EducationIcon>
                  <EducationContent>
                    <EducationDegree>BSc in Electrical &amp; Electronics Engineering</EducationDegree>
                    <EducationSchool>Manisa Celal Bayar University</EducationSchool>
                  </EducationContent>
                </EducationItem>
                <ExpertiseText>
                  My academic pursuits keep me aligned with emerging innovations, 
                  enabling me to architect end-to-end solutions that align with 
                  modern best practices.
                </ExpertiseText>
              </ExpertiseContent>
            </ExpertiseSection>

            {/* Contact Section */}
            <ContactSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.9 }}
            >
              <SectionTitle>Get in Touch</SectionTitle>
              <ExpertiseContent>
                <ExpertiseText>
                  Have a new project, collaboration idea, or simply want to chat about 
                  cutting-edge software development? Reach out—I'm eager to connect 
                  and bring fresh ideas to life.
                </ExpertiseText>
                <SocialLinks>
                  <SocialIcon
                    href="mailto:denizeren.gencay@cndsoftware.com"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = 'mailto:denizeren.gencay@cndsoftware.com';
                    }}
                    title="Send email"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Mail size={20} />
                  </SocialIcon>
                  <SocialIcon
                    href="https://linkedin.com/in/denizgencay"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn Profile"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Linkedin size={20} />
                  </SocialIcon>
                  <SocialIcon
                    href="https://github.com/denizgencay"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="GitHub Profile"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Github size={20} />
                  </SocialIcon>
                </SocialLinks>
              </ExpertiseContent>
            </ContactSection>
          </ProfileInfo>
        </ProfileSection>
      </MemberContainer>
    </PageContainer>
  );
};

export default DenizErenGencay;